import { ForwardedRef, forwardRef, InputHTMLAttributes } from 'react';
import { BaseFormState } from '~/types/BaseFormState';
import { FormState } from 'react-hook-form';
import styles from './Field.module.css';
import classNames from 'classnames';
import { get } from 'lodash';
import { HasClassName } from '~/types/HasClassName';

export type Props<TFormState extends BaseFormState> = Pick<
  InputHTMLAttributes<HTMLInputElement>,
  | 'inputMode'
  | 'onKeyDown'
  | 'onFocus'
  | 'onChange'
  | 'onBlur'
  | 'defaultValue'
  | 'placeholder'
  | 'autoComplete'
  | 'value'
  | 'name'
  | 'id'
  | 'required'
  | 'readOnly'
  | 'disabled'
  | 'style'
  | 'step'
  | 'min'
  | 'max'
  | 'aria-label'
> &
  HasClassName & {
    type: 'text' | 'password' | 'email' | 'tel' | 'number' | 'date' | 'search' | 'file';
    formState?: FormState<TFormState>;
  };

function FieldElement<TFormState extends BaseFormState>(props: Props<TFormState>, ref: ForwardedRef<HTMLInputElement>) {
  const { formState, name, className, ...rest } = props;

  return (
    <input
      className={classNames(styles.input, className, {
        [styles.error]: name && !!get(formState?.errors, name)?.message,
      })}
      name={name}
      ref={ref}
      {...rest}
    />
  );
}

export const Field = forwardRef(FieldElement) as <TFormState extends BaseFormState>(
  props: Props<TFormState> & { ref?: React.ForwardedRef<HTMLInputElement> }
) => ReturnType<typeof FieldElement>;
